/**
 * variant-swatches.js (v2.2.3)
 * PDP: single product mount (#variant-swatches-root)
 * Collection cards: batch scan ([data-variant-swatches-root])
 */
(function () {
  "use strict";

  if (window.__variantSwatches) {
    window.__variantSwatches.initProduct();
    window.__variantSwatches.scanCards();
    return;
  }

  var VERSION = "2.3.0";
  var COLLAPSED_LIMIT = 4;
  var IMAGE_WIDTH = 500;
  var requestCache =
    window.__variantSwatchesRequestCache ||
    (window.__variantSwatchesRequestCache = {});

  var cardsInFlight = false;
  var cardsPendingRescan = false;

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function isProductPage() {
    return document.body && document.body.classList.contains("template-product");
  }

  function stopEvent(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function normalizeGroup(raw) {
    if (!raw) return null;
    if (raw.group) return normalizeGroup(raw.group);
    if (raw.products && raw.products.length) return raw;
    return null;
  }

  function getTotalCount(group) {
    return group.product_count || group.products.length;
  }

  function productKey(product) {
    return String(product.product_id || product.handle || product.option_value);
  }

  function normalizeColorName(value) {
    return String(value || "")
      .toLowerCase()
      .trim()
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ");
  }

  function colorSlug(value) {
    return normalizeColorName(value).replace(/\s+/g, "-");
  }

  function findProductByColor(products, preferredColor) {
    if (!preferredColor || !products || !products.length) return null;
    var wanted = normalizeColorName(preferredColor);
    var wantedSlug = colorSlug(preferredColor);

    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      var candidates = [
        product.option_value,
        product.color,
        product.color_name,
        product.name,
        product.title,
      ];
      for (var c = 0; c < candidates.length; c++) {
        var normalized = normalizeColorName(candidates[c]);
        if (!normalized) continue;
        if (normalized === wanted || colorSlug(candidates[c]) === wantedSlug) {
          return product;
        }
      }
    }
    return null;
  }

  function findProductByHandle(products, preferredHandle) {
    if (!preferredHandle || !products || !products.length) return null;
    var wanted = String(preferredHandle).toLowerCase();
    for (var i = 0; i < products.length; i++) {
      if (String(products[i].handle || "").toLowerCase() === wanted) {
        return products[i];
      }
    }
    return null;
  }

  function getPreferredSelection(mount) {
    return {
      handle: mount.getAttribute("data-selected-handle") || "",
      color: mount.getAttribute("data-selected-color") || "",
    };
  }

  function orderProductsWithSelectedFirst(products, selectedKey) {
    if (!products || !products.length) return [];
    if (!selectedKey) return products.slice();

    var selected = null;
    var rest = [];
    for (var i = 0; i < products.length; i++) {
      if (productKey(products[i]) === selectedKey) {
        selected = products[i];
      } else {
        rest.push(products[i]);
      }
    }

    if (!selected) return products.slice();
    return [selected].concat(rest);
  }

  function pickVisibleProducts(products, limit, selectedKey) {
    return orderProductsWithSelectedFirst(products, selectedKey).slice(0, limit);
  }

  function isFeaturedSlideshowMount(mount) {
    if (!mount || !mount.closest) return false;
    return !!mount.closest(
      ".wt-featured-collection:not(.wt-featured-collection--recommendation) slideshow-section"
    );
  }

  function getCollapsedLimit(mount) {
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 899px)").matches &&
      isFeaturedSlideshowMount(mount)
    ) {
      return 3;
    }
    return COLLAPSED_LIMIT;
  }

  function ensureSelectedKey(mount, products) {
    var preferred = getPreferredSelection(mount);
    var preferredHandle = preferred.handle;
    var preferredColor = preferred.color;
    var existingKey = mount.dataset.vsSelectedKey || "";

    if (preferredColor) {
      var byColor = findProductByColor(products, preferredColor);
      if (byColor) {
        mount.dataset.vsSelectedKey = productKey(byColor);
        return;
      }
    }

    if (existingKey) return;

    if (preferredHandle) {
      var byHandle = findProductByHandle(products, preferredHandle);
      if (byHandle) {
        mount.dataset.vsSelectedKey = productKey(byHandle);
        return;
      }
    }

    for (var i = 0; i < products.length; i++) {
      if (products[i].active) {
        mount.dataset.vsSelectedKey = productKey(products[i]);
        return;
      }
    }
  }

  function isProductSelected(product, selectedKey) {
    if (selectedKey) return productKey(product) === selectedKey;
    return !!product.active;
  }

  function getMountConfig(el) {
    return {
      productId: el.getAttribute("data-product-id") || "",
      apiBase: el.getAttribute("data-api-base") || "",
      shop: el.getAttribute("data-shop") || "",
    };
  }

  function cacheKey(cfg, type) {
    return [type, cfg.apiBase, cfg.shop, cfg.productId].join("|");
  }

  function fetchVariantGroupsRaw(cfg) {
    var key = cacheKey(cfg, "single");
    if (!requestCache[key]) {
      var url =
        cfg.apiBase +
        "/variant-groups?productId=" +
        encodeURIComponent(cfg.productId) +
        "&shop=" +
        encodeURIComponent(cfg.shop);

      requestCache[key] = fetch(url, { credentials: "omit" })
        .then(function (res) {
          if (!res.ok) throw new Error("variant-groups request failed: " + res.status);
          return res.json();
        })
        .catch(function (err) {
          delete requestCache[key];
          throw err;
        });
    }
    return requestCache[key];
  }

  function fetchSingleGroup(cfg) {
    return fetchVariantGroupsRaw(cfg).then(normalizeGroup);
  }

  function getMaterialItems(materials) {
    if (!materials || !materials.items || !materials.items.length) return [];
    return materials.items;
  }

  function buildDot(product) {
    var dot = document.createElement("span");
    dot.className = "vs-swatch__dot";
    dot.style.backgroundColor = product.color_hex || "#ccc";
    return dot;
  }

  function createWrapper(group) {
    var wrapper = document.createElement("div");
    wrapper.className = "vs-swatch-group";
    wrapper.setAttribute("role", "group");
    wrapper.setAttribute("aria-label", group.name || "Color options");
    wrapper.setAttribute("data-vs-version", VERSION);

    var swatchRow = document.createElement("div");
    swatchRow.className = "vs-swatch-group__swatches";
    wrapper.appendChild(swatchRow);

    return { wrapper: wrapper, swatchRow: swatchRow };
  }

  // --- PDP ---

  function buildPdpSwatch(product) {
    var link = document.createElement("a");
    link.href = "/products/" + product.handle;
    link.className = "vs-swatch" + (product.active ? " vs-swatch--active" : "");
    link.title = product.option_value;
    link.setAttribute("aria-label", product.option_value);
    if (product.active) {
      link.setAttribute("aria-current", "true");
      link.addEventListener("click", stopEvent);
    }
    link.appendChild(buildDot(product));
    return link;
  }

  function isMaterialActive(item, materials) {
    if (item.active) return true;
    if (materials && materials.current && item.name) {
      return String(item.name).toLowerCase() === String(materials.current).toLowerCase();
    }
    return false;
  }

  function buildPdpMaterialSwatch(item, materials) {
    var active = isMaterialActive(item, materials);
    var link = document.createElement("a");
    link.href = "/products/" + item.handle;
    link.className = "vs-material" + (active ? " vs-material--active" : "");
    link.title = item.name || "";
    link.setAttribute("aria-label", item.name || "Fabric");
    if (active) {
      link.setAttribute("aria-current", "true");
      link.addEventListener("click", stopEvent);
    }

    var imgUrl = (item.image_urls && item.image_urls[0]) || "";
    if (imgUrl) {
      var img = document.createElement("img");
      img.src = withImageWidth(imgUrl, 160);
      img.alt = item.name || "";
      img.width = 40;
      img.height = 40;
      img.loading = "lazy";
      link.appendChild(img);
    } else {
      var fallback = document.createElement("span");
      fallback.className = "vs-material__fallback";
      fallback.textContent = (item.name || "?").charAt(0);
      link.appendChild(fallback);
    }
    return link;
  }

  function buildMaterialsGroup(materials) {
    var items = getMaterialItems(materials);
    var wrapper = document.createElement("div");
    wrapper.className = "vs-material-group";
    wrapper.setAttribute("role", "group");
    wrapper.setAttribute("aria-label", "Fabric options");

    var head = document.createElement("div");
    head.className = "vs-material-group__head";
    var label = document.createElement("span");
    label.className = "vs-material-group__label";
    label.textContent = "Fabric: ";
    var current = document.createElement("strong");
    current.className = "vs-material-group__current";
    current.textContent = materials.current || "";
    head.appendChild(label);
    head.appendChild(current);
    wrapper.appendChild(head);

    var row = document.createElement("div");
    row.className = "vs-material-group__items";
    items.forEach(function (item) {
      row.appendChild(buildPdpMaterialSwatch(item, materials));
    });
    wrapper.appendChild(row);
    return wrapper;
  }

  function renderPdp(mountEl, payload) {
    var group = normalizeGroup(payload);
    var materials = payload && payload.materials;
    var products = (group && group.products) || [];
    var materialItems = getMaterialItems(materials);

    mountEl.innerHTML = "";

    if (materialItems.length > 1) {
      mountEl.appendChild(buildMaterialsGroup(materials));
    }

    if (products.length) {
      var parts = createWrapper(group);
      products.forEach(function (product) {
        parts.swatchRow.appendChild(buildPdpSwatch(product));
      });
      mountEl.appendChild(parts.wrapper);
    }
  }

  function isMountRendered(mount) {
    return mount.dataset.vsInitialized === "true" &&
      (mount.querySelector(".vs-swatch-group") || mount.querySelector(".vs-material-group"));
  }

  function resetStuckMount(mount) {
    var rendered = mount.querySelector(".vs-swatch-group");
    if (!rendered && mount.dataset.vsInitialized !== "false") {
      mount.dataset.vsInitialized = "false";
    }
  }

  function initProduct() {
    var root = qs("#variant-swatches-root");
    if (!root) return;

    resetStuckMount(root);
    if (isMountRendered(root)) return;
    if (root.dataset.vsInitialized === "pending") return;

    var cfg = getMountConfig(root);
    if (!cfg.productId || !cfg.apiBase || !cfg.shop) return;

    root.dataset.vsInitialized = "pending";

    fetchVariantGroupsRaw(cfg)
      .then(function (payload) {
        if (!payload) return;
        renderPdp(root, payload);
        root.dataset.vsInitialized = "true";
      })
      .catch(function (err) {
        root.dataset.vsInitialized = "false";
        if (window.console) console.error("[variant-swatches]", err);
      });
  }

  // --- Collection cards ---

  function withImageWidth(url, width) {
    if (!url) return url;
    if (/[?&]width=/.test(url)) return url;
    return url + (url.indexOf("?") === -1 ? "?" : "&") + "width=" + width;
  }

  function getCardContext(mount) {
    var colorSwatch = mount.closest("color-swatch");
    if (!colorSwatch) return null;

    var container = colorSwatch.querySelector(".card__container");
    if (!container) return null;

    var imgs = container.querySelectorAll(".card__img");
    return {
      pictureLink: container.querySelector(".card__picture-container a.card"),
      titleLink: container.querySelector(".card__title a"),
      img: imgs[0] || null,
      hoverImg: imgs[1] || null,
      loader: container.querySelector(".card__loader"),
      video: container.querySelector("video"),
      quickAdds: colorSwatch.querySelectorAll("quick-add"),
    };
  }

  function showCardLoader(cardCtx) {
    if (!cardCtx || !cardCtx.loader || !cardCtx.img) return;
    cardCtx.loader.classList.remove("hidden");
    cardCtx.loader.innerHTML = '<div class="spinner-ring"></div>';
    cardCtx.img.style.opacity = "0";
  }

  function hideCardLoader(cardCtx) {
    if (!cardCtx || !cardCtx.loader || !cardCtx.img) return;
    cardCtx.loader.classList.add("hidden");
    cardCtx.loader.innerHTML = "";
    cardCtx.img.style.opacity = "1";
  }

  function setAttributes(element, attrs) {
    if (!element) return;
    Object.keys(attrs).forEach(function (key) {
      element.setAttribute(key, attrs[key]);
    });
  }

  function syncMountProductIdentity(mount, product) {
    if (!mount || !product) return;

    if (product.product_id) {
      mount.setAttribute("data-product-id", String(product.product_id));
    }

    var colorSwatch = mount.closest("color-swatch");
    if (!colorSwatch) return;

    if (product.handle) {
      colorSwatch.setAttribute("data-current-variant-handle", product.handle);
    }
    if (product.product_id) {
      colorSwatch.setAttribute("data-current-product-id", String(product.product_id));
    }
  }

  function asGid(type, id) {
    if (id == null || id === "") return null;
    var s = String(id);
    if (s.indexOf("gid://") === 0) return s;
    return "gid://shopify/" + type + "/" + s;
  }

  /** 色卡切换后同步 Snowplow product_clicked props，避免点到旧商品 */
  function syncCardTrackProps(cardCtx, product) {
    if (!cardCtx || !product) return;
    var links = [cardCtx.pictureLink, cardCtx.titleLink];
    var imageUrls = product.image_urls || [];
    var href = product.handle ? "/products/" + product.handle : null;

    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      if (!link || !link.getAttribute) continue;
      if (link.getAttribute("data-track") !== "product_clicked") continue;

      var props = {};
      try {
        props = JSON.parse(link.getAttribute("data-track-props") || "{}") || {};
      } catch (e) {
        props = {};
      }

      if (product.product_id) props.item_id = asGid("Product", product.product_id);
      if (product.title) props.item_name = product.title;
      if (product.vendor) props.item_brand = product.vendor;
      if (product.product_type) props.item_category = product.product_type;
      if (product.price != null && product.price !== "") props.price = Number(product.price);
      if (href) props.product_url = href;
      if (imageUrls[0]) props.image_url = String(imageUrls[0]).slice(0, 255);
      if (product.option_value) props.item_variant = product.option_value;
      if (product.variant_id) props.variant_id = asGid("ProductVariant", product.variant_id);
      if (product.sku != null) props.sku = product.sku;

      link.setAttribute("data-track-props", JSON.stringify(props));
    }
  }

  function applyCardProduct(cardCtx, product, mount) {
    if (!cardCtx || !product) return;

    var href = "/products/" + product.handle;
    var imageUrls = product.image_urls || [];
    var mainImg = withImageWidth(imageUrls[0] || "", IMAGE_WIDTH);
    var hoverImg = withImageWidth(imageUrls[1] || "", IMAGE_WIDTH);

    setAttributes(cardCtx.pictureLink, { href: href });
    setAttributes(cardCtx.titleLink, { href: href });
    syncCardTrackProps(cardCtx, product);

    if (cardCtx.titleLink && product.title) {
      cardCtx.titleLink.textContent = product.title;
    }

    if (!mainImg || !cardCtx.img) return;

    showCardLoader(cardCtx);
    cardCtx.img.classList.remove("hidden");
    cardCtx.img.onload = function () {
      hideCardLoader(cardCtx);
      cardCtx.img.onload = null;
    };

    setAttributes(cardCtx.img, { src: mainImg });
    cardCtx.img.removeAttribute("srcset");

    if (cardCtx.hoverImg && hoverImg) {
      setAttributes(cardCtx.hoverImg, { src: hoverImg });
      cardCtx.hoverImg.removeAttribute("srcset");
      cardCtx.hoverImg.classList.remove("hidden");
    } else if (cardCtx.hoverImg) {
      cardCtx.hoverImg.classList.add("hidden");
    }

    if (cardCtx.video) {
      cardCtx.video.classList.add("hidden");
    }

    Array.prototype.forEach.call(cardCtx.quickAdds || [], function (quickAdd) {
      quickAdd.setAttribute("data-product-handle", product.handle);
      var btn = quickAdd.querySelector("button");
      if (btn) btn.dataset.productUrl = href;
    });

    if (mount) {
      syncMountProductIdentity(mount, product);
    }
  }

  function setActiveSwatch(swatchRow, activeEl) {
    swatchRow.querySelectorAll(".vs-swatch").forEach(function (swatch) {
      swatch.classList.remove("vs-swatch--active");
      swatch.removeAttribute("aria-current");
    });
    activeEl.classList.add("vs-swatch--active");
    activeEl.setAttribute("aria-current", "true");
  }

  function buildCardSwatch(product, mount, swatchRow, selectedKey) {
    var isActive = isProductSelected(product, selectedKey);
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "vs-swatch" + (isActive ? " vs-swatch--active" : "");
    btn.title = product.option_value;
    btn.setAttribute("aria-label", product.option_value);
    btn.dataset.vsProductKey = productKey(product);
    if (isActive) {
      btn.setAttribute("aria-current", "true");
    }
    btn.appendChild(buildDot(product));

    btn.addEventListener("click", function (e) {
      stopEvent(e);
      mount.dataset.vsSelectedKey = productKey(product);
      applyCardProduct(getCardContext(mount), product, mount);
      setActiveSwatch(swatchRow, btn);
    });

    return btn;
  }

  function appendCardSwatches(container, products, mount, swatchRow, selectedKey) {
    products.forEach(function (product) {
      container.appendChild(buildCardSwatch(product, mount, swatchRow, selectedKey));
    });
  }

  function buildCollapseBtn(mount) {
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "vs-swatch-group__collapse";
    btn.setAttribute("aria-label", "Collapse color options");
    btn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" aria-hidden="true" focusable="false">' +
      '<path fill="currentColor" d="M6 3.8 2.2 7.6h7.6L6 3.8z"/>' +
      "</svg>";
    btn.addEventListener("mousedown", stopEvent);
    btn.addEventListener("click", function (e) {
      stopEvent(e);
      renderCard(mount, mount._vsFullGroup || mount._vsBatchGroup);
    });
    return btn;
  }

  function buildMoreBtn(mount, hiddenCount) {
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "vs-swatch-group__more";
    btn.textContent = "+" + hiddenCount;
    btn.setAttribute("aria-label", "Show " + hiddenCount + " more color options");
    btn.addEventListener("mousedown", stopEvent);
    btn.addEventListener("click", function (e) {
      stopEvent(e);
      if (btn.disabled) return;

      btn.disabled = true;
      var originalText = btn.textContent;
      btn.textContent = "...";

      fetchSingleGroup(getMountConfig(mount))
        .then(function (fullGroup) {
          if (!fullGroup) throw new Error("variant-groups response missing products");
          mount._vsFullGroup = fullGroup;
          renderCard(mount, fullGroup, { expanded: true });
        })
        .catch(function (err) {
          btn.disabled = false;
          btn.textContent = originalText;
          if (window.console) console.error("[variant-swatches]", err);
        });
    });
    return btn;
  }

  function renderCard(mount, group, options) {
    if (!group) return;

    var forceExpanded = options && options.expanded;
    var products = group.products || [];
    var totalCount = getTotalCount(group);

    ensureSelectedKey(mount, products);
    var selectedKey = mount.dataset.vsSelectedKey || "";

    if (forceExpanded) {
      mount._vsFullGroup = group;
    } else if (!mount._vsBatchGroup) {
      mount._vsBatchGroup = group;
    }

    var parts = createWrapper(group);
    var collapsedLimit = getCollapsedLimit(mount);

    if (!forceExpanded && totalCount > collapsedLimit) {
      parts.wrapper.classList.add("vs-swatch-group--collapsed");
      var visible = pickVisibleProducts(products, collapsedLimit, selectedKey);
      var hiddenCount = Math.max(0, totalCount - collapsedLimit);

      appendCardSwatches(parts.swatchRow, visible, mount, parts.swatchRow, selectedKey);
      if (hiddenCount > 0) {
        parts.swatchRow.appendChild(buildMoreBtn(mount, hiddenCount));
      }
    } else {
      var orderedProducts = orderProductsWithSelectedFirst(products, selectedKey);
      appendCardSwatches(parts.swatchRow, orderedProducts, mount, parts.swatchRow, selectedKey);
      parts.wrapper.classList.add("vs-swatch-group--expanded");
      if (totalCount > collapsedLimit) {
        parts.swatchRow.appendChild(buildCollapseBtn(mount));
      }
    }

    mount.innerHTML = "";
    mount.appendChild(parts.wrapper);
  }

  function getGroupForProduct(groups, productId) {
    if (!groups || !productId) return null;
    return (
      groups[productId] ||
      groups[String(productId)] ||
      groups[Number(productId)] ||
      null
    );
  }

  function finishCardsBatch() {
    cardsInFlight = false;
    if (cardsPendingRescan) {
      cardsPendingRescan = false;
      scanCards();
    }
  }

  function scanCards() {
    if (isProductPage()) return;

    var mounts = Array.prototype.slice.call(
      document.querySelectorAll("[data-variant-swatches-root]")
    );
    var pending = mounts.filter(function (mount) {
      if (isMountRendered(mount)) return false;
      resetStuckMount(mount);
      return (
        mount.dataset.vsInitialized !== "true" &&
        mount.dataset.vsInitialized !== "pending"
      );
    });

    if (!pending.length) return;
    if (cardsInFlight) {
      cardsPendingRescan = true;
      return;
    }

    var cfg = getMountConfig(pending[0]);
    if (!cfg.apiBase || !cfg.shop) return;

    var productIds = [];
    pending.forEach(function (mount) {
      var productId = mount.getAttribute("data-product-id");
      if (!productId) return;
      if (productIds.indexOf(productId) === -1) productIds.push(productId);
      mount.dataset.vsInitialized = "pending";
    });

    if (!productIds.length) return;

    cardsInFlight = true;

    var batchKey = cacheKey(
      { apiBase: cfg.apiBase, shop: cfg.shop, productId: productIds.join(",") },
      "batch"
    );

    if (!requestCache[batchKey]) {
      var url =
        cfg.apiBase +
        "/variant-groups?shop=" +
        encodeURIComponent(cfg.shop) +
        "&productIds=" +
        encodeURIComponent(productIds.join(","));

      requestCache[batchKey] = fetch(url, { credentials: "omit" })
        .then(function (res) {
          if (!res.ok) throw new Error("variant-groups request failed: " + res.status);
          return res.json();
        })
        .catch(function (err) {
          delete requestCache[batchKey];
          throw err;
        });
    }

    requestCache[batchKey]
      .then(function (data) {
        var groups = (data && data.groups) || {};

        pending.forEach(function (mount) {
          var productId = mount.getAttribute("data-product-id");
          var group = normalizeGroup(getGroupForProduct(groups, productId));

          if (group) {
            renderCard(mount, group);
            mount.dataset.vsInitialized = "true";
            return;
          }

          mount.remove();
          mount.dataset.vsInitialized = "true";
        });
      })
      .catch(function (err) {
        pending.forEach(function (mount) {
          mount.dataset.vsInitialized = "false";
        });
        if (window.console) console.error("[variant-swatches]", err);
      })
      .finally(finishCardsBatch);
  }

  function observeProductGrid() {
    if (observeProductGrid._observed) return;
    observeProductGrid._observed = true;

    var grid = document.getElementById("product-grid");
    if (!grid || typeof MutationObserver === "undefined") return;

    var timer;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(scanCards, 80);
    }).observe(grid, { childList: true, subtree: true });
  }

  function bootstrap() {
    initProduct();
    scanCards();
    observeProductGrid();
  }

  window.__variantSwatches = {
    initProduct: initProduct,
    scanCards: scanCards,
  };
  window.VariantSwatches = window.__variantSwatches;
  window.VariantSwatchesCards = { scan: scanCards };

  // Start as early as possible on PDP; fallback on DOMContentLoaded.
  bootstrap();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap, { once: true });
  }

  window.addEventListener("pageshow", bootstrap);
})();
